import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { absoluteTime, relativeTime } from "../lib/format";

// Screen 1 — Fleet view (Design 2.7 #1).
// Reactive: tenants -> devices (online/offline, version, tier, health, last
// seen) + a per-device drill-in reading deviceDetail. Empty tables render an
// empty state gracefully (Phase-0 milestone is "empty fleet renders").
export function FleetView() {
  const fleet = useQuery(api.fleet.fleetView, {});
  const [selected, setSelected] = useState<Id<"devices"> | null>(null);

  return (
    <div className="screen">
      <header className="screen-head">
        <h1>Fleet</h1>
        <p className="screen-sub">
          Every enrolled device by tenant. Online = heartbeat within 90s.
        </p>
      </header>

      {fleet === undefined ? (
        <Skeleton />
      ) : fleet.length === 0 ? (
        <EmptyState
          title="No tenants yet"
          body="Seed a tenant + enrollment token, then enroll a device. The fleet appears here live."
        />
      ) : (
        <div className="tenant-list">
          {fleet.map((t) => (
            <section className="tenant-block" key={t.tenantId}>
              <div className="tenant-head">
                <h2>{t.tenantName}</h2>
                <LaneBadge kind={t.kind} telemetryLane={t.telemetryLane} />
                <span className="muted">{t.devices.length} device(s)</span>
              </div>

              {t.kind === "customer" && (
                <p className="tenant-note muted">
                  Moat lane — no customer content is available in the cloud by
                  design. Only operational data (devices, versions, health) is
                  shown.
                </p>
              )}

              {t.devices.length === 0 ? (
                <div className="row-empty">No devices enrolled.</div>
              ) : (
                <table className="grid">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Host</th>
                      <th>Tier</th>
                      <th>Channel</th>
                      <th>Version</th>
                      <th>Status</th>
                      <th>Last seen</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.devices.map((d) => (
                      <tr key={d._id}>
                        <td className="strong">{d.deviceName}</td>
                        <td className="muted">{d.hostname}</td>
                        <td>{d.hardwareTier ?? "—"}</td>
                        <td>
                          <span className="chip">{d.channel}</span>
                        </td>
                        <td>{d.appVersion ?? "—"}</td>
                        <td>
                          <StatusDot online={d.online} status={d.status} />
                        </td>
                        <td className="muted">
                          {relativeTime(d.lastHeartbeatAt)}
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setSelected(d._id)}
                          >
                            Inspect
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          ))}
        </div>
      )}

      {selected && (
        <DeviceDrawer deviceId={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

// Visually distinguishes tenant-zero (DEV lane) from customer tenants (MOAT
// lane). The moat is structural: customer devices carry devTelemetry=false, so
// no content ever reaches the cloud. The badge makes that boundary legible at a
// glance in the console.
function LaneBadge({
  kind,
  telemetryLane,
}: {
  kind: "tenant_zero" | "customer";
  telemetryLane: "dev" | "moat";
}) {
  const isCustomer = kind === "customer";
  const label = isCustomer ? "customer (MOAT)" : "tenant-zero (DEV)";
  return (
    <span
      className={`tag tag-${kind} lane-${telemetryLane}`}
      title={
        isCustomer
          ? "Customer tenant on the moat lane — no content reaches the cloud."
          : "Mike's DEV-lane test fleet — the only tenant with content telemetry."
      }
    >
      {label}
    </span>
  );
}

function StatusDot({ online, status }: { online: boolean; status: string }) {
  const cls = online ? "ok" : status === "error" ? "err" : "off";
  return (
    <span className={`status status-${cls}`}>
      <span className="dot" /> {online ? "online" : status}
    </span>
  );
}

function DeviceDrawer({
  deviceId,
  onClose,
}: {
  deviceId: Id<"devices">;
  onClose: () => void;
}) {
  const detail = useQuery(api.fleet.deviceDetail, { deviceId, eventLimit: 50 });

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <h2>Device detail</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        {detail === undefined ? (
          <Skeleton />
        ) : detail === null ? (
          <EmptyState title="Device not found" body="It may have been removed." />
        ) : (
          <div className="drawer-body">
            <dl className="kv">
              <dt>Name</dt>
              <dd>{detail.deviceName}</dd>
              <dt>Hostname</dt>
              <dd>{detail.hostname}</dd>
              <dt>OS</dt>
              <dd>{detail.osVersion ?? "—"}</dd>
              <dt>Tier</dt>
              <dd>{detail.hardwareTier ?? "—"}</dd>
              <dt>Channel</dt>
              <dd>
                <span className="chip">{detail.channel}</span>
              </dd>
              <dt>App version</dt>
              <dd>{detail.appVersion ?? "—"}</dd>
              <dt>Status</dt>
              <dd>{detail.status}</dd>
              <dt>Last heartbeat</dt>
              <dd>{absoluteTime(detail.lastHeartbeatAt)}</dd>
              <dt>Desired release</dt>
              <dd className="mono">{detail.desiredReleaseId ?? "—"}</dd>
              <dt>Current release</dt>
              <dd className="mono">{detail.currentReleaseId ?? "—"}</dd>
            </dl>

            <h3 className="section-title">Recent health events</h3>
            {detail.recentHealth.length === 0 ? (
              <div className="row-empty">No health events yet.</div>
            ) : (
              <ul className="event-log">
                {detail.recentHealth.map((h) => (
                  <li key={h._id}>
                    <span className={`evt-kind evt-${h.kind}`}>{h.kind}</span>
                    <span className="muted">{relativeTime(h.ts)}</span>
                    {h.detailJson != null && (
                      <code className="evt-detail">
                        {JSON.stringify(h.detailJson)}
                      </code>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {detail.profileJson != null && (
              <>
                <h3 className="section-title">Hardware profile</h3>
                <pre className="json-block">
                  {JSON.stringify(detail.profileJson, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      <div className="empty-body">{body}</div>
    </div>
  );
}

function Skeleton() {
  return <div className="skeleton">Loading…</div>;
}
